import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import FavoriteButton from "@/components/feature/FavoriteButton";
import SharePanel from "@/components/feature/SharePanel";
import SectionHeading from "@/components/feature/SectionHeading";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/lib/seo";
import { useFavorites } from "@/hooks/useFavorites";
import {
  formatDate,
  getMovieById,
  getRelatedMovies,
  mediaTypeLabel,
  newsList,
  type Movie,
} from "@/lib/media";
import { generalReviews, reviews } from "@/mocks/reviews";

function ScoreStars({ score }: { score: number }) {
  const starValue = score / 2;
  return (
    <span className="flex items-center gap-0.5" aria-label={`评分 ${score.toFixed(1)} 分`}>
      {[1, 2, 3, 4, 5].map((index) => {
        const icon =
          starValue >= index
            ? "ri-star-fill"
            : starValue >= index - 0.5
              ? "ri-star-half-fill"
              : "ri-star-line";
        return (
          <span key={index} className="flex h-3.5 w-3.5 items-center justify-center text-accent-500">
            <i className={`${icon} text-sm`} />
          </span>
        );
      })}
    </span>
  );
}

function DetailShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main className="pt-[72px]">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-8">{children}</div>
      </main>
      <SiteFooter />
    </div>
  );
}

export default function DetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const movie = getMovieById(id);
  const [shareOpen, setShareOpen] = useState(false);
  const { isMovieFavorite, toggleMovie } = useFavorites();

  usePageMeta({
    title: movie ? `${movie.title} - 影视详情及介绍 - 枫与铃` : "影视详情 - 枫与铃",
    description: movie
      ? `《${movie.title}》${movie.year}年${movie.region}${mediaTypeLabel[movie.type]}，由${movie.director}执导，${movie.cast.slice(0, 2).join("、")}主演。${movie.summary}`
      : "查看影视作品详情、评分、剧情简介与相关推荐。",
    keywords: movie
      ? `${movie.title},${movie.title}剧情,${mediaTypeLabel[movie.type]}推荐,枫与铃`
      : "影视详情,枫与铃",
  });

  const related = useMemo<Movie[]>(() => (movie ? getRelatedMovies(movie, 4) : []), [movie]);
  const relatedNews = useMemo(() => {
    if (!movie) return [];
    const matched = newsList.filter((item) => item.relatedMovieId === movie.id);
    return matched.length > 0 ? matched.slice(0, 2) : newsList.slice(0, 2);
  }, [movie]);

  if (!movie) {
    return (
      <DetailShell>
        <EmptyState
          icon="ri-file-search-line"
          title="这部作品不存在或已下架"
          description="可能是链接输入有误，返回首页看看其他值得一看的作品。"
          actionLabel="返回首页"
          onAction={() => navigate("/")}
        />
      </DetailShell>
    );
  }

  const favorite = isMovieFavorite(movie.id);
  const movieReviews = reviews.filter((review) => review.movieId === movie.id);
  const reviewList = movieReviews.length > 0 ? movieReviews : generalReviews;
  const distribution = [62, 23, 9, 4, 2];
  const relatedGenres = movie.genres.join(" / ");

  const infoRows = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join(" / ") },
    { label: "类型", value: movie.genres.join(" / ") },
    { label: "地区", value: movie.region },
    { label: "语言", value: movie.language },
    { label: "年份", value: `${movie.year}` },
    { label: "片长", value: movie.duration },
    { label: "上映日期", value: formatDate(movie.releaseDate) },
  ];

  return (
    <div className="min-h-screen bg-background-50 pb-20 md:pb-0">
      <SiteHeader />

      <main className="pt-[72px]">
        <section className="relative">
          <div className="relative h-[220px] w-full overflow-hidden bg-foreground-950 md:h-[320px]">
            <img
              src={movie.backdrop}
              alt={`${movie.title}横版剧照`}
              title={`${movie.title} 影视详情`}
              className="h-full w-full object-cover object-top opacity-70"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/30 to-foreground-950/40" />
            <nav
              className="absolute inset-x-0 top-4 mx-auto flex w-full max-w-[1280px] flex-wrap items-center gap-2 px-4 text-xs text-background-50/80 md:px-8"
              aria-label="面包屑"
            >
              <Link to="/" className="cursor-pointer hover:text-background-50">
                首页
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line text-sm" />
              </span>
              <Link
                to={`/category/${movie.type}`}
                className="cursor-pointer hover:text-background-50"
              >
                {mediaTypeLabel[movie.type]}
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line text-sm" />
              </span>
              <span className="text-background-50">{movie.title}</span>
            </nav>
          </div>

          <div className="mx-auto w-full max-w-[1280px] px-4 md:px-8">
            <div className="relative -mt-20 flex flex-col gap-6 md:-mt-28 md:flex-row md:gap-8">
              <div className="relative aspect-[2/3] w-[132px] shrink-0 overflow-hidden rounded-lg border-4 border-background-50 bg-background-200 md:w-[220px]">
                <img
                  src={movie.poster}
                  alt={`${movie.title}海报`}
                  title={`${movie.title} 影视详情`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="flex min-w-0 flex-1 flex-col justify-end pb-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-primary-500 px-2 py-0.5 text-[11px] font-medium text-background-50">
                    {mediaTypeLabel[movie.type]}
                  </span>
                  {movie.genres.map((genre) => (
                    <span
                      key={genre}
                      className="rounded border border-background-300 bg-background-50/80 px-2 py-0.5 text-[11px] text-foreground-600"
                    >
                      {genre}
                    </span>
                  ))}
                </div>
                <h1 className="mt-3 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
                  {movie.title}
                </h1>
                <p className="mt-2 text-sm text-foreground-600">
                  {movie.year} · {movie.region} · {movie.duration} · {movie.language}
                </p>

                <div className="mt-4 flex flex-wrap items-center gap-4">
                  <div className="flex items-end gap-2">
                    <span className="font-heading text-3xl font-bold leading-none text-primary-600">
                      {movie.score.toFixed(1)}
                    </span>
                    <div className="pb-0.5">
                      <ScoreStars score={movie.score} />
                      <span className="mt-1 block text-[11px] text-foreground-500">
                        基于虚构评分数据
                      </span>
                    </div>
                  </div>
                  <span className="hidden h-8 w-px bg-background-300 sm:block" />
                  <span className="text-xs text-foreground-500">
                    站内热度 {movie.hot} · 收录于{mediaTypeLabel[movie.type]}
                  </span>
                </div>

                <div className="mt-5 hidden flex-wrap items-center gap-3 md:flex">
                  <FavoriteButton
                    active={favorite}
                    onClick={() => toggleMovie(movie.id)}
                    label={favorite ? "已加入收藏" : "加入我的收藏"}
                  />
                  <button
                    type="button"
                    onClick={() => setShareOpen(true)}
                    className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-share-forward-line text-base" />
                    </span>
                    分享
                  </button>
                  <span className="text-[11px] text-foreground-500">
                    本站不提供在线播放与下载
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-12">
          <div className="grid gap-8 lg:grid-cols-[1.8fr_1fr] lg:gap-10">
            <div className="min-w-0">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <h2 className="font-heading text-lg font-semibold text-foreground-950">剧情简介</h2>
                <p className="mt-3 text-sm leading-7 text-foreground-700">{movie.synopsis}</p>
                <p className="mt-4 rounded-md bg-background-100 px-4 py-3 text-xs leading-relaxed text-foreground-600">
                  {movie.summary}　全片围绕「{relatedGenres}」展开，适合喜欢{relatedGenres}题材的观众。
                </p>
              </div>

              <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="font-heading text-lg font-semibold text-foreground-950">用户评价</h2>
                  <span className="text-xs text-foreground-500">示例评价 · 共 {reviewList.length} 条</span>
                </div>

                <div className="mt-5 flex flex-col gap-6 sm:flex-row">
                  <div className="shrink-0 sm:w-40">
                    <div className="flex items-end gap-2">
                      <span className="font-heading text-4xl font-bold leading-none text-foreground-950">
                        {movie.score.toFixed(1)}
                      </span>
                      <span className="pb-1 text-xs text-foreground-500">/ 10</span>
                    </div>
                    <div className="mt-2">
                      <ScoreStars score={movie.score} />
                    </div>
                    <p className="mt-2 text-xs text-foreground-500">虚构观众评分</p>
                  </div>
                  <div className="flex-1 space-y-2">
                    {distribution.map((percent, index) => (
                      <div key={percent} className="flex items-center gap-2">
                        <span className="w-6 text-[11px] text-foreground-500">{5 - index} 星</span>
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-background-200">
                          <div
                            className="h-full rounded-full bg-accent-500"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <span className="w-8 text-right text-[11px] text-foreground-500">
                          {percent}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 divide-y divide-background-200">
                  {reviewList.map((review) => (
                    <div key={review.id} className="py-4 first:pt-0 last:pb-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary-100 font-heading text-xs font-semibold text-secondary-700">
                          {review.user.slice(0, 1)}
                        </span>
                        <span className="text-sm font-medium text-foreground-900">{review.user}</span>
                        <span className="text-[11px] text-foreground-500">{formatDate(review.date)}</span>
                        <span className="ml-auto flex items-center gap-1 text-xs text-accent-600">
                          <span className="flex h-3.5 w-3.5 items-center justify-center">
                            <i className="ri-star-fill text-sm" />
                          </span>
                          {review.rating}.0
                        </span>
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                        {review.content}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-10">
                <SectionHeading
                  eyebrow="You May Also Like"
                  title="相关推荐"
                  description="根据类型与地区相似度、站内热度综合排序。"
                  actionLabel={`更多${mediaTypeLabel[movie.type]}`}
                  actionTo={`/category/${movie.type}`}
                />
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
                  {related.map((item) => (
                    <MovieCard key={item.id} movie={item} />
                  ))}
                </div>
              </div>
            </div>

            <aside className="flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-100/70 p-5">
                <h2 className="font-heading text-base font-semibold text-foreground-950">影片信息</h2>
                <dl className="mt-4 flex flex-col gap-3">
                  {infoRows.map((row) => (
                    <div key={row.label} className="flex gap-3 text-sm">
                      <dt className="w-16 shrink-0 text-foreground-500">{row.label}</dt>
                      <dd className="min-w-0 flex-1 text-foreground-800">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-semibold text-foreground-950">相关资讯</h2>
                <ul className="mt-3 flex flex-col">
                  {relatedNews.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/news/${item.id}`}
                        className="group flex cursor-pointer gap-3 border-b border-background-200 py-3 last:border-b-0"
                      >
                        <div className="h-14 w-20 shrink-0 overflow-hidden rounded bg-background-200">
                          <img
                            src={item.cover}
                            alt={item.title}
                            title={`${item.title} 影视资讯`}
                            className="h-full w-full object-cover object-top"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="line-clamp-2 text-xs font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                            {item.title}
                          </p>
                          <span className="mt-1 block text-[11px] text-foreground-500">
                            {formatDate(item.date)}
                          </span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-dashed border-background-300 bg-background-100 p-5">
                <div className="flex items-center gap-2 text-primary-600">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-information-line text-base" />
                  </span>
                  <span className="text-xs font-medium">观影提示</span>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  本站仅提供影视作品介绍与资讯浏览，不提供在线播放、下载或购票链接。评分与评价均为演示数据。
                </p>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <div className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 border-t border-background-200 bg-background-50/95 px-4 py-3 backdrop-blur md:hidden">
        <button
          type="button"
          onClick={() => toggleMovie(movie.id)}
          className={`flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border px-4 py-2.5 text-sm font-medium ${
            favorite
              ? "border-primary-300 bg-primary-50 text-primary-700"
              : "border-background-300 text-foreground-700"
          }`}
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className={favorite ? "ri-heart-3-fill text-base" : "ri-heart-3-line text-base"} />
          </span>
          {favorite ? "已收藏" : "收藏"}
        </button>
        <button
          type="button"
          onClick={() => setShareOpen(true)}
          className="flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-share-forward-line text-base" />
          </span>
          分享
        </button>
      </div>

      <SharePanel open={shareOpen} title={`${movie.title} - 影视详情 - 枫与铃`} onClose={() => setShareOpen(false)} />
      <SiteFooter />
    </div>
  );
}